/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ImportCodesModal.tsx
 🗓️ Created: 2025-01-28
 🧩 Description:
    Modal for importing authentication codes from CSV file.

 🔧 Notes:
    - Supports CSV file upload
    - Validates file format
    - Shows import results

 🔁 Last Updated: 2025-01-28 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useRef, useEffect } from 'react';
import { Upload, X, AlertCircle, CheckCircle, XCircle, Info } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { importAuthenticationCodes } from './api';
import { ImportCodesResult } from './types';

interface ImportCodesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ruleId: string | number;
}


export default function ImportCodesModal({ isOpen, onClose, onSuccess, ruleId }: ImportCodesModalProps) {
  const { t } = useI18n();
  const { showError, showSuccess } = useNotifications();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);
  const [importMode, setImportMode] = useState<'skip' | 'update' | 'replace'>('skip');
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ImportCodesResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset modal state when it opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setCsvContent(null);
      setRowCount(0);
      setResults(null);
      setError(null);
      setImportMode('skip');
      setIsProcessing(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError(t('authenticationCodes.import.invalidFileType'));
      return;
    }

    setSelectedFile(file);
    setError(null);
    setResults(null);

    // Read CSV file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;

        // Validate CSV has content
        if (!content || content.trim().length === 0) {
          setError(t('authenticationCodes.import.invalidFileFormat'));
          setCsvContent(null);
          return;
        }

        // Count rows (excluding header)
        const lines = content.split('\n').filter(line => line.trim());
        const count = lines.length > 1 ? lines.length - 1 : 0;

        setRowCount(count);
        setCsvContent(content);
      } catch (err) {
        setError(t('authenticationCodes.import.invalidCsv'));
        setCsvContent(null);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvContent) return;

    setIsProcessing(true);
    setError(null);

    try {
      const result = await importAuthenticationCodes(ruleId, {
        csvContent,
        mode: importMode,
      });

      setResults(result);
      if (result) {
        showSuccess(t('authenticationCodes.import.success'));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('authenticationCodes.import.error');
      setError(message);
      showError(message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    // If there are results, refresh the parent data
    if (results && results.created >= 0) {
      onSuccess();
    }

    setSelectedFile(null);
    setCsvContent(null);
    setRowCount(0);
    setResults(null);
    setError(null);
    setImportMode('skip');
    onClose();
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Upload className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              {t('authenticationCodes.import.title')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* File Selection */}
          {!results && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('authenticationCodes.import.selectFile')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    onClick={handleBrowse}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('authenticationCodes.import.browse')}
                  </button>
                  {selectedFile && (
                    <span className="text-sm text-muted-foreground">
                      {selectedFile.name}
                    </span>
                  )}
                </div>
              </div>

              {/* CSV Format Info */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-2 text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-100">
                      {t('authenticationCodes.import.csvFormat')}
                    </p>
                    <div className="text-blue-700 dark:text-blue-300 font-mono text-xs bg-white dark:bg-gray-800 p-2 rounded">
                      code,label,enabled<br />
                      1234,VIP Customer,true<br />
                      5678,Partner,true<br />
                      9999,Admin,false
                    </div>
                  </div>
                </div>
              </div>

              {/* File Info */}
              {csvContent && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-100">
                        {t('authenticationCodes.import.fileInfo')}
                      </p>
                      <ul className="text-blue-700 dark:text-blue-300 space-y-1">
                        <li>• {t('authenticationCodes.import.totalCodes')}: {rowCount}</li>
                        <li>• {t('authenticationCodes.import.fileFormat')}: CSV</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Import Mode */}
              {csvContent && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-foreground">
                    {t('authenticationCodes.import.mode')}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input
                        type="radio"
                        name="importMode"
                        value="skip"
                        checked={importMode === 'skip'}
                        onChange={(e) => setImportMode(e.target.value as any)}
                        className="text-primary"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {t('authenticationCodes.import.modeSkip')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('authenticationCodes.import.modeSkipDesc')}
                        </div>
                      </div>
                    </label>

                    <label className="flex items-center gap-2 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input
                        type="radio"
                        name="importMode"
                        value="update"
                        checked={importMode === 'update'}
                        onChange={(e) => setImportMode(e.target.value as any)}
                        className="text-primary"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {t('authenticationCodes.import.modeUpdate')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('authenticationCodes.import.modeUpdateDesc')}
                        </div>
                      </div>
                    </label>

                    <label className="flex items-center gap-2 p-3 border border-border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input
                        type="radio"
                        name="importMode"
                        value="replace"
                        checked={importMode === 'replace'}
                        onChange={(e) => setImportMode(e.target.value as any)}
                        className="text-primary"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {t('authenticationCodes.import.modeReplace')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('authenticationCodes.import.modeReplaceDesc')}
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-4">
              {/* Success Banner */}
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                      {t('authenticationCodes.import.success')}
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300">
                      {results.created} {t('authenticationCodes.import.codesCreated')}, {results.updated} {t('authenticationCodes.import.codesUpdated')}
                      {results.skipped > 0 && `, ${results.skipped} ${t('authenticationCodes.import.codesSkipped')}`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-900 dark:text-green-100">
                      {t('authenticationCodes.import.created')}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                    {results.created}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    {t('authenticationCodes.import.createdDesc')}
                  </div>
                </div>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      {t('authenticationCodes.import.updated')}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    {results.updated}
                  </div>
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    {t('authenticationCodes.import.updatedDesc')}
                  </div>
                </div>

                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    <span className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                      {t('authenticationCodes.import.skipped')}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">
                    {results.skipped}
                  </div>
                  <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    {t('authenticationCodes.import.skippedDesc')}
                  </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Info className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('authenticationCodes.import.total')}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                    {results.created + results.updated + results.skipped + results.errors.length}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {t('authenticationCodes.import.totalDesc')}
                  </div>
                </div>
              </div>

              {/* Errors */}
              {results.errors && results.errors.length > 0 && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 flex-1">
                      <p className="text-sm font-medium text-red-900 dark:text-red-100">
                        {t('authenticationCodes.import.errors')} ({results.errors.length})
                      </p>
                      <ul className="text-xs text-red-700 dark:text-red-300 space-y-1 max-h-40 overflow-y-auto">
                        {results.errors.map((err, idx) => (
                          <li key={idx}>• Row {err.row}: {err.code} - {err.message}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
          {!results ? (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                disabled={isProcessing}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleImport}
                disabled={!csvContent || isProcessing}
                className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('authenticationCodes.import.processing')}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {t('authenticationCodes.import.import')}
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t('common.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

